import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Users, Plus, KeyRound, UserX, UserCheck, Loader2, ScanLine, Pencil } from "lucide-react";
import { listUsers, createUser, updateUser, resetUserPassword, getMezzi } from "../lib/api";
import { togglePinLogin, adminResetPin } from "../lib/api_extra";
import { ConfirmDialog } from "../components/ConfirmDialog";

const RUOLI = [
  { v: "AMMINISTRATORE", label: "Amministratore", color: "#1B5E20" },
  { v: "UFFICIO", label: "Ufficio", color: "#1565C0" },
  { v: "TRASPORTATORE", label: "Trasportatore", color: "#F57C00" },
];

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openReset, setOpenReset] = useState(null);
  const [openResetPin, setOpenResetPin] = useState(null);
  const [form, setForm] = useState({
    email: "", password: "", nome: "", cognome: "", ruolo: "UFFICIO", mezzoIdAssegnato: "",
  });
  const [resetPwd, setResetPwd] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([listUsers(), getMezzi()]);
      setUsers(u);
      setMezzi(m);
    } catch (e) {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const submitCreate = async () => {
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        nome: form.nome,
        cognome: form.cognome,
        ruolo: form.ruolo,
        mezzoIdAssegnato: form.ruolo === "TRASPORTATORE" ? (form.mezzoIdAssegnato || null) : null,
      };
      await createUser(payload);
      toast.success("Utente creato");
      setOpenCreate(false);
      setForm({ email: "", password: "", nome: "", cognome: "", ruolo: "UFFICIO", mezzoIdAssegnato: "" });
      await reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore creazione");
    }
  };

  const toggleAttivo = async (u) => {
    try {
      await updateUser(u.id, { attivo: !u.attivo });
      toast.success(u.attivo ? "Utente disattivato" : "Utente riattivato");
      await reload();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Errore"); }
  };

  const submitReset = async () => {
    if (resetPwd.length < 8 || !/\d/.test(resetPwd)) {
      toast.error("Min 8 caratteri + 1 numero");
      return;
    }
    try {
      await resetUserPassword(openReset.id, resetPwd);
      toast.success("Password resettata. L'utente la cambierà al prossimo login.");
      setOpenReset(null);
      setResetPwd("");
    } catch (ex) { toast.error(ex.response?.data?.detail || "Errore"); }
  };

  const [openEdit, setOpenEdit] = useState(null);
  const [editForm, setEditForm] = useState({});

  const submitEdit = async () => {
    try {
      const payload = {
        email: editForm.email,
        nome: editForm.nome,
        cognome: editForm.cognome,
      };
      if (openEdit.ruolo === "TRASPORTATORE") {
        payload.mezzoIdAssegnato = editForm.mezzoIdAssegnato || null;
      }
      await updateUser(openEdit.id, payload);
      toast.success("Utente aggiornato");
      setOpenEdit(null);
      await reload();
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Errore aggiornamento");
    }
  };

  const onTogglePin = async (u) => {
    try {
      await togglePinLogin(u.id, !u.loginConPin);
      toast.success(u.loginConPin ? "Login con PIN disabilitato" : "Login con PIN abilitato");
      await reload();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Errore toggle PIN"); }
  };

  const submitResetPin = async () => {
    if (!openResetPin) return;
    try {
      await adminResetPin(openResetPin.id);
      toast.success("PIN azzerato. Al prossimo login l'utente dovrà reimpostarlo.");
      setOpenResetPin(null);
      await reload();
    } catch (ex) { toast.error(ex.response?.data?.detail || "Errore reset PIN"); }
  };

  const mezzoNome = (mid) => mezzi.find((m) => m.id === mid)?.nome || "-";

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Gestione utenti
          </h1>
          <p className="text-slate-500 text-sm mt-1">Crea, disattiva, resetta password</p>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]" data-testid="btn-new-user">
          <Plus size={16} className="mr-1" /> Nuovo utente
        </Button>
      </div>

      {loading ? (
        <Card className="p-8 text-center"><Loader2 size={28} className="mx-auto animate-spin text-[#1B5E20]" /></Card>
      ) : (
        <div className="grid gap-2">
          {users.map((u) => {
            const ruolo = RUOLI.find((r) => r.v === u.ruolo);
            return (
              <Card key={u.id} className={`p-4 ${u.attivo ? "" : "opacity-60"}`} data-testid={`user-row-${u.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                       style={{ backgroundColor: ruolo?.color, fontFamily: "Outfit, sans-serif" }}>
                    {(u.nome?.[0] || u.email[0]).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-semibold text-slate-900">{u.nome} {u.cognome}</div>
                      <Badge style={{ backgroundColor: ruolo?.color, color: "#fff" }} className="text-[10px]">{ruolo?.label}</Badge>
                      {u.loginConPin && <Badge className="bg-[#F57C00] text-white text-[10px]">PIN</Badge>}
                      {!u.attivo && <Badge className="bg-slate-400 text-white text-[10px]">DISATTIVO</Badge>}
                      {u.mustChangePassword && <Badge className="bg-[#1565C0] text-white text-[10px]">CAMBIO PWD</Badge>}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{u.email}</div>
                    {u.ruolo === "TRASPORTATORE" && (
                      <div className="text-xs text-slate-500 mt-0.5" data-testid={`user-mezzo-info-${u.id}`}>
                        Mezzo: {u.mezzoIdAssegnato ? mezzoNome(u.mezzoIdAssegnato) : "nessun mezzo"}
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">
                      Ultimo accesso: {u.ultimoAccesso ? new Date(u.ultimoAccesso).toLocaleString("it-IT") : "mai"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="outline"
                      onClick={() => { setOpenEdit(u); setEditForm({ email: u.email, nome: u.nome, cognome: u.cognome, mezzoIdAssegnato: u.mezzoIdAssegnato || "" }); }}
                      className="text-xs min-h-[36px]" data-testid={`edit-user-${u.id}`}>
                      <Pencil size={12} className="mr-1" /> Modifica
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setOpenReset(u)} className="text-xs min-h-[36px]" data-testid={`reset-pwd-${u.id}`}>
                      <KeyRound size={12} className="mr-1" /> Reset PWD
                    </Button>
                    {u.ruolo === "TRASPORTATORE" && (
                      <>
                        <div className="flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200" data-testid={`pin-toggle-${u.id}`}>
                          <ScanLine size={12} className="text-[#F57C00]" />
                          <span className="text-[10px] font-medium text-slate-600">PIN</span>
                          <Switch
                            checked={u.loginConPin}
                            onCheckedChange={() => onTogglePin(u)}
                            data-testid={`pin-switch-${u.id}`}
                            className="ml-auto scale-75"
                          />
                        </div>
                        {u.loginConPin && u.pinSetUp && (
                          <Button size="sm" variant="outline" onClick={() => setOpenResetPin(u)}
                            className="text-xs min-h-[36px] text-[#F57C00] border-[#F57C00] hover:bg-[#F57C00]/10"
                            data-testid={`reset-pin-${u.id}`}>
                            <ScanLine size={12} className="mr-1" /> Reset PIN
                          </Button>
                        )}
                      </>
                    )}
                    <Button size="sm" variant="outline" onClick={() => toggleAttivo(u)} className="text-xs min-h-[36px]" data-testid={`toggle-active-${u.id}`}>
                      {u.attivo ? <><UserX size={12} className="mr-1" />Disattiva</> : <><UserCheck size={12} className="mr-1" />Riattiva</>}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog creazione utente */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo utente</DialogTitle>
            <DialogDescription>L'utente sarà obbligato a cambiare la password al primo login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="user-nome" /></div>
              <div><Label>Cognome</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="user-cognome" /></div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
            </div>
            <div>
              <Label>Password temporanea *</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
              <p className="text-[10px] text-slate-400 mt-1">Min 8 caratteri + 1 numero. L'utente la cambierà al primo login.</p>
            </div>
            <div>
              <Label>Ruolo *</Label>
              <Select value={form.ruolo} onValueChange={(v) => setForm({ ...form, ruolo: v })}>
                <SelectTrigger data-testid="user-ruolo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RUOLI.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.ruolo === "TRASPORTATORE" && (
              <div>
                <Label>Mezzo assegnato *</Label>
                <Select value={form.mezzoIdAssegnato} onValueChange={(v) => setForm({ ...form, mezzoIdAssegnato: v })}>
                  <SelectTrigger data-testid="user-mezzo"><SelectValue placeholder="Seleziona mezzo" /></SelectTrigger>
                  <SelectContent>
                    {mezzi.filter((m) => m.attivo).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.nome} ({m.targa})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Annulla</Button>
            <Button onClick={submitCreate} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="user-create-btn">Crea utente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog reset password */}
      <Dialog open={!!openReset} onOpenChange={(v) => !v && setOpenReset(null)}>
        <DialogContent data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Imposta una password temporanea per {openReset?.email}. L'utente sarà disconnesso da tutti i device e dovrà cambiarla al prossimo login.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Nuova password temporanea</Label>
            <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-pwd-input" autoFocus />
            <p className="text-[10px] text-slate-400 mt-1">Min 8 caratteri + 1 numero</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenReset(null)}>Annulla</Button>
            <Button onClick={submitReset} className="bg-[#F57C00] hover:bg-[#E65100] text-white" data-testid="reset-pwd-confirm">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica utente (BUG #8: ruolo readonly) */}
      <Dialog open={!!openEdit} onOpenChange={(v) => !v && setOpenEdit(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Modifica utente</DialogTitle>
            <DialogDescription>{openEdit?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Nome</Label><Input value={editForm.nome || ""} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} data-testid="edit-user-nome" /></div>
              <div><Label>Cognome</Label><Input value={editForm.cognome || ""} onChange={(e) => setEditForm({ ...editForm, cognome: e.target.value })} data-testid="edit-user-cognome" /></div>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" />
            </div>
            {/* RUOLO readonly (BUG #8) */}
            <div>
              <Label>Ruolo</Label>
              <div className="px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-sm" data-testid="edit-user-ruolo-readonly">
                {RUOLI.find((r) => r.v === openEdit?.ruolo)?.label || openEdit?.ruolo}
              </div>
              <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                Il ruolo è fissato alla creazione e non può essere modificato.
                Per cambiare il ruolo di un utente, disattivare l'account esistente e crearne uno nuovo con il ruolo desiderato.
              </p>
            </div>
            {openEdit?.ruolo === "TRASPORTATORE" && (
              <div>
                <Label>Mezzo assegnato (abituale)</Label>
                <Select value={editForm.mezzoIdAssegnato || "_none_"} onValueChange={(v) => setEditForm({ ...editForm, mezzoIdAssegnato: v === "_none_" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-mezzo"><SelectValue placeholder="Seleziona mezzo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none_">Nessuno</SelectItem>
                    {mezzi.filter((m) => m.attivo).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.nome} ({m.targa})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(null)}>Annulla</Button>
            <Button onClick={submitEdit} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="edit-user-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!openResetPin}
        onCancel={() => setOpenResetPin(null)}
        onConfirm={submitResetPin}
        title="Azzerare il PIN?"
        description={`Il PIN di ${openResetPin?.nome} ${openResetPin?.cognome} verrà rimosso. Al prossimo login l'utente dovrà reimpostare PIN, domanda di recupero e risposta. La sessione attuale verrà chiusa.`}
        confirmLabel="Azzera PIN"
        variant="warning"
        testId="reset-pin-dialog"
      />
    </div>
  );
}
